import Image from 'next/image'
import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'

import { FaInstagram, FaFacebook, FaTrophy } from 'react-icons/fa'
import { FaSquareXTwitter } from 'react-icons/fa6'
import { IoLogoYoutube } from 'react-icons/io5'
import { AiOutlineFlag, AiOutlineClose } from 'react-icons/ai'
import { FiActivity, FiPlusCircle, FiLink } from 'react-icons/fi'
import { PiUserCircle, PiHouse, PiChartLine, PiAirTrafficControlBold, PiChatCircle } from "react-icons/pi";
import { LiaCoinsSolid } from "react-icons/lia";
import { differenceInDays, parseISO, isValid } from 'date-fns'
import { 
  Listbox,
  ListboxItem,
  Chip,
  Button, 
  useDisclosure} from '@heroui/react'

import { useUser } from '@/contexts/user-context-v2'
import ChallengeImage from './header-image'
import useChallenges from '@/hooks/use-challenges'
import { challengeSorter } from '@/src/sorters'
import { formatNumber } from '@/src/formatter'
import { impersonate } from '@/app/lib/impersonate'
import LoadingModal from '@/components/loading-modal'
import { loadBeacon } from '@/app/lib/help-scout'

const Menu = ({ setIsMenuOpen, challengeId, section, page }) => {
  const { user } = useUser()
  const path = usePathname()
  const router = useRouter()
  
  const challengeData = useChallenges(user?.token)
  const { activeChallenges } = challengeData

  const adminModal = useDisclosure()

  const backtoAdmin = async () => {
    // reverse impersonation back to admin
    adminModal.onOpen()
    await impersonate(user.adminRef.token, user.adminRef.id)
    window.location.href = '/account/profile'
  }
  
  const menuItems = [{
    key: 'challenges',
    url: '/challenges',
    name: 'Challenges',
    icon: <AiOutlineFlag className='text-chTan-600 mr-1 text-2xl' />
  }, {
    key: 'activities',
    url: '/activities',
    name: 'Activities',
    icon: <FiActivity className='text-chTan-600 mr-1 text-2xl' />
  }, {
    key: 'apps',
    url: '/apps',
    name: 'Apps',
    icon: <FiLink className='text-chTan-600 mr-1 text-2xl' />
  }, {
    key: 'account',
    url: '/account',
    name: 'Account',
    icon: <PiUserCircle className='text-chTan-600 mr-1 text-2xl' />
  }]

  if (user?.organizationId) {
    menuItems.push({
      key: 'credits', 
      url: '/account/credits',
      name: `${formatNumber(user.organization.balance, 1)} Credits`,
      icon: <LiaCoinsSolid className='text-chTan-600 mr-1  text-2xl' />,
      end: user.organization.balance < 3 && <Chip size='sm' color='danger' className='mr-4'>Low Balance</Chip>
    })
  }

  menuItems.push({
    key: 'support',
    name: 'Help Center',
    onPress: () => {
      loadBeacon(true, user)
    },
    icon: <PiChatCircle className='text-chTan-600 mr-1 text-2xl' />
  })

  if (user?.adminRef && user?.adminRef.id !== user?.id) {
    menuItems.push({
      key: 'back-to-admin', 
      name: 'Back to Admin',
      onPress: backtoAdmin,
      icon: <PiAirTrafficControlBold className='text-primary mr-1  text-2xl' />
    })
  }

  // Match the end of the path instead of checking for exact template patterns
  if (path.endsWith('/feed')) {
    page = 'challenge-feed'
  } else if (path.endsWith('/dashboard')) {
    page = 'dashboard'
  } else if (path.endsWith('/activities')) {
    page = 'activities'
  }

  return <div className='h-screen flex flex-col'>
    <div className='absolute top-4 left-2 md:hidden'>
      <Button isIconOnly className='bg-chTan-200' onPress={() => { setIsMenuOpen(false) }}>
        <AiOutlineClose className='text-chTan-600 text-2xl' />
      </Button>
    </div>
    <div className='md:mt-0 mb-2 p-4 flex justify-center md:block'>
      <Image src='/challenge-hound-logo.png' alt='Challenge Hound' width={145} height={51} className='md:hidden' />
      <Image src='/challenge-hound-logo.png' alt='Challenge Hound' width={250} height={88} className='hidden md:block' />
    </div>
    
    <div className='flex-grow md:overflow-y-scroll'>
      {activeChallenges && activeChallenges.sort(challengeSorter)
        .map((challenge) => {
          let minimize = false
          if (challenge.completed) {
            const parsed = parseISO(challenge.completed);
            if (!isValid(parsed)) return true
            const diff = differenceInDays(new Date(), new Date(challenge.completed))
            // show in menu for 10 ten days and then hide. 
            if (diff > 10) minimize = true
          }
          
          const challengeMenuItems = [{
            key: `${challenge.id}-challenge-feed`,
            url: `/challenge/${challenge.id}/feed`,
            name: 'Feed',
            icon: <PiHouse className='text-chTan-600 mr-1 text-2xl' />,
            on: challengeId === challenge.id && section === 'challenges' && page === 'challenge-feed'
          }, {
            key: `${challenge.id}-overview`,
            url: `/challenge/${challenge.id}/dashboard`,
            name: 'Dashboard',
            icon: <PiChartLine className='text-chTan-600 mr-1 text-2xl' />,
            on: challengeId === challenge.id && section === 'challenges' && page === 'dashboard'
          }, {
            key: `${challenge.id}-activities`,
            url: `/challenge/${challenge.id}/activities`,
            name: 'Activites',
            icon: <FiActivity className='text-chTan-600 mr-1 text-2xl' />,
            on: challengeId === challenge.id && section === 'challenges' && page === 'activities'
          }]

          return <div key={challenge.id} className='bg-chTan-200 rounded-md mb-4 mx-4'>
            <div className='flex items-center p-2 cursor-pointer' onClick={() => {
              router.push(`/challenge/${challenge.id}/feed`)
            }}>
              <div className='min-w-[48px] min-h-[48px] rounded-full overflow-hidden relative mr-2'>
                <ChallengeImage name={challenge.challengeName} image={challenge.logo} />
              </div>
              <div className='grow text-md font-bold line-clamp-2 leading-tight'>
                {challenge.challengeName}
              </div>
              {minimize && <FaTrophy className='text-secondary text-2xl mx-1 min-w-6' />}
            </div>
            
            {!minimize && <Listbox 
              aria-label="Main Menu" 
              hideSelectedIcon
              selectionMode='single'
              autoFocus={true}
              className='w-full'
              itemClasses={{ 
                base: 'rounded-none text-chDarkBlue data-[hover=true]:bg-chTan-300', 
                title: 'text-md' 
              }}
              >
                {challengeMenuItems.map((item) => <ListboxItem
                  title={item.name}
                  key={item.key}
                  as={Link}
                  href={item.url}
                  prefetch={false}
                  startContent={item.icon}
                  endContent ={item.end}
                  onPress={() => {
                    if (setIsMenuOpen) setIsMenuOpen(false) // hide menu
                  }}
                  className={`${item.on ? 'bg-chTan-100' : ''}`} 
                  classNames={{ title: `text-xl ${item.on ? 'font-semibold' : ''}` }}>
                </ListboxItem>)}
            </Listbox>}
          </div>
        })
      }

      <div className='bg-chTan-200 flex justify-center mx-4 my-4 p-2 py-3'>
        <Button
          variant=''
          size=''
          className=''
          startContent={<FiPlusCircle className='text-2xl text-primary mr-2' />}
          as={Link} 
          href='/houndroid'>Create a Challenge</Button>
      </div>
  
      <Listbox 
        aria-label="Main Menu" 
        hideSelectedIcon
        selectionMode='single'
        autoFocus={true}
        className=''
        itemClasses={{ 
          base: 'rounded-none text-chDarkBlue data-[hover=true]:bg-chTan-300', 
          
          title: 'text-md' 
        }}
        >
          {menuItems.map((item) => <ListboxItem
            title={item.name}
            textvalue={item.name}
            key={item.key}
            as={item.url ? Link : undefined}
            href={item.url}
            prefetch={item.url ? false : undefined}
            target={item.external ? '_blank' : ''}
            startContent={item.icon}
            endContent ={item.end}
            onPress={() => {
              if (item.onPress) item.onPress()
              if (setIsMenuOpen) setIsMenuOpen(false) // hide menu
            }}
            className={`ml-4 text-xl hover:bg-chTan-300` } >
          </ListboxItem>)}
      </Listbox>
    </div>

    {(user?.adminRef && user?.adminRef.id !== user?.id) && <LoadingModal text='Reverting to admin user...' modal={adminModal} />}

    <div className='p-4 text-sm mt-8 md:mt-0 w-full md:w-[300px]'>
      <div className='text-center text-chTan-600 mb-1'>&copy; Challenge Hound LLC</div>
      <div className='grid grid-cols-4 text-left'>
        <div><Link prefetch={false} className='text-chTan-600' href='/privacy'>Privacy</Link></div>
        <div><Link prefetch={false} className='text-chTan-600' href='/cookies'>Cookies</Link></div>
        <div><Link prefetch={false} className='text-chTan-600' href='/terms'>Terms</Link></div>
        <div className='flex items-center justify-center text-lg mr-1'>
          <div className='mr-2'><a href='https://x.com/ChallengeHound'><FaSquareXTwitter className='text-chTan-600' /></a></div>
          <div className='mr-2'><a href='https://instagram.com/challengehound'><FaInstagram  className='text-chTan-600' /></a></div>
          <div className='mr-2'><a href='https://facebook.com/challengehound'><FaFacebook  className='text-chTan-600' /></a></div>
          <div className=''><a href='https://www.youtube.com/@challengehound4806'><IoLogoYoutube  className='text-chTan-600' /></a></div>
        </div>
      </div>
    </div>
  </div>
}

export default Menu